import { Stack } from '@src/common/components/Stack';
import { theme } from '@src/common/styles/Theme';
import type { CollapseListener } from '@src/orders/CollapseListener';
import { EditOrderCollapsible } from '@src/orders/EditOrderCollapsible';
import type { FormEntries } from '@src/orders/FormEntries';
import { useSectionError } from '@src/orders/hooks/useSectionError';
import { ConditionalField } from '@src/orders/PermitFormSections/ConditionalField';
import { ControllerFormCheckbox } from '@src/orders/PermitFormSections/FormCheckbox';
import { FormDimensionInput } from '@src/orders/PermitFormSections/FormDimensionInput';
import { FormWeightInput } from '@src/orders/PermitFormSections/FormWeightInput';
import { useDisabledFields } from '@src/orders/PermitFormSections/useDisabledFields';
import {
  TractorActualWeightField,
  tractorDimensionsFields,
  TractorGroundHeightField,
  TractorGVWField,
  TractorHeightField,
  TractorLegalWeight,
  TractorLengthField,
  TractorOuterBridgeLengthField,
  TractorUnladenWeightField,
  TractorWheelBaseField,
  TractorWidthField,
} from '@src/permitField/TractorDimensionsFields';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

type TractorInformation = FormEntries<string[]>;

type Props = { fields: string[] } & CollapseListener;

export const TractorDimensionsSection = (props: Props) => {
  const { t } = useTranslation();
  const { control } = useFormContext<TractorInformation>();
  const { isFieldDisabled } = useDisabledFields();

  const { sectionInError } = useSectionError(
    Object.keys(tractorDimensionsFields),
  );

  if (props.fields.length <= 0) {
    return null;
  }

  return (
    <EditOrderCollapsible
      title={t('tractorDimensions')}
      id="tractor-dimensions"
      collapseEventName={props.collapseEventName}
      uncollapseEventName={props.uncollapseEventName}
      inError={sectionInError}
    >
      <Stack gap={theme.spacing['3']}>
        <Stack direction="column" gap={theme.spacing['3']}>
          <ConditionalField
            fields={props.fields}
            conditions={TractorLegalWeight.fieldName}
          >
            <ControllerFormCheckbox
              control={control}
              fieldName={TractorLegalWeight.fieldName}
              disabled={isFieldDisabled(TractorLegalWeight.fieldName)}
            />
          </ConditionalField>
          <ConditionalField
            fields={props.fields}
            conditions={TractorLengthField.fieldName}
          >
            <FormDimensionInput
              control={control}
              fieldName={TractorLengthField.fieldName}
              disabled={isFieldDisabled(TractorLengthField.fieldName)}
            />
          </ConditionalField>
          <ConditionalField
            fields={props.fields}
            conditions={TractorWidthField.fieldName}
          >
            <FormDimensionInput
              control={control}
              fieldName={TractorWidthField.fieldName}
              disabled={isFieldDisabled(TractorWidthField.fieldName)}
            />
          </ConditionalField>
          <ConditionalField
            fields={props.fields}
            conditions={TractorHeightField.fieldName}
          >
            <FormDimensionInput
              control={control}
              fieldName={TractorHeightField.fieldName}
              disabled={isFieldDisabled(TractorHeightField.fieldName)}
            />
          </ConditionalField>
          <ConditionalField
            fields={props.fields}
            conditions={TractorWheelBaseField.fieldName}
          >
            <FormDimensionInput
              control={control}
              fieldName={TractorWheelBaseField.fieldName}
              disabled={isFieldDisabled(TractorWheelBaseField.fieldName)}
            />
          </ConditionalField>
          <ConditionalField
            fields={props.fields}
            conditions={TractorGroundHeightField.fieldName}
          >
            <FormDimensionInput
              control={control}
              fieldName={TractorGroundHeightField.fieldName}
              disabled={isFieldDisabled(TractorGroundHeightField.fieldName)}
            />
          </ConditionalField>

          <ConditionalField
            fields={props.fields}
            conditions={TractorUnladenWeightField.fieldName}
          >
            <FormWeightInput
              control={control}
              fieldName={TractorUnladenWeightField.fieldName}
              multiplyByThousand={true}
              disabled={isFieldDisabled(TractorUnladenWeightField.fieldName)}
            />
          </ConditionalField>
          <ConditionalField
            fields={props.fields}
            conditions={TractorActualWeightField.fieldName}
          >
            <FormWeightInput
              control={control}
              fieldName={TractorActualWeightField.fieldName}
              multiplyByThousand={true}
              disabled={isFieldDisabled(TractorActualWeightField.fieldName)}
            />
          </ConditionalField>
          <ConditionalField
            fields={props.fields}
            conditions={TractorGVWField.fieldName}
          >
            <FormWeightInput
              control={control}
              fieldName={TractorGVWField.fieldName}
              multiplyByThousand={true}
              disabled={isFieldDisabled(TractorGVWField.fieldName)}
            />
          </ConditionalField>
          <ConditionalField
            fields={props.fields}
            conditions={TractorOuterBridgeLengthField.fieldName}
          >
            <FormDimensionInput
              control={control}
              fieldName={TractorOuterBridgeLengthField.fieldName}
              disabled={isFieldDisabled(
                TractorOuterBridgeLengthField.fieldName,
              )}
            />
          </ConditionalField>
        </Stack>
      </Stack>
    </EditOrderCollapsible>
  );
};
